import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Arrow, Hotspot, ScenarioImage } from '../game/imageTypes';

/** How each arrow should be rendered once the answers are revealed. */
export type Verdict = 'found' | 'missed' | 'false-positive' | 'correct-reject';

interface Props {
  image: ScenarioImage;
  arrows: readonly Arrow[];
  selected: ReadonlySet<string>;
  onToggle: (arrowId: string) => void;
  /** When present the game is over and arrows show their outcome. */
  reveal?: ReadonlyMap<string, Verdict>;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;
/** Movement in px beyond which a press is a drag, not a tap. */
const TAP_SLOP = 8;

interface View {
  scale: number;
  tx: number;
  ty: number;
}

/**
 * Pan/zoom surface for an annotated screenshot, with the red arrows as
 * selectable targets.
 *
 * A real tool screenshot is far too dense to judge at fit-width on a phone, so
 * pinch-zoom is mandatory. That means three gestures all beginning with a
 * finger on glass have to be told apart: a tap (toggle an arrow), a drag (pan),
 * and a two-finger pinch (zoom). Pointer Events unify mouse and touch, and
 * `touch-action: none` stops the browser hijacking the gesture first.
 *
 * Arrow regions are percentages of the image, never pixels, so a region defined
 * once lands correctly at any display size.
 */
export function ImageViewer({ image, arrows, selected, onToggle, reveal }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [view, setView] = useState<View>({ scale: 1, tx: 0, ty: 0 });

  // Mirror of `view` for gesture math, so handlers never read stale state.
  const viewRef = useRef(view);
  viewRef.current = view;

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef({
    mode: 'none' as 'none' | 'pan' | 'pinch',
    startX: 0,
    startY: 0,
    moved: 0,
    startDistance: 0,
    startView: { scale: 1, tx: 0, ty: 0 } as View,
  });

  // Size of the image when scaled to fit the container at scale 1.
  const fit = useMemo(() => {
    if (!size.w || !size.h || !image.width || !image.height) return { w: 0, h: 0 };
    const ratio = Math.min(size.w / image.width, size.h / image.height);
    return { w: image.width * ratio, h: image.height * ratio };
  }, [size, image.width, image.height]);

  const clamp = useCallback(
    (next: View): View => {
      const scaledW = fit.w * next.scale;
      const scaledH = fit.h * next.scale;
      // Centre while the image is smaller than the viewport; otherwise stop its
      // edges being dragged inside the viewport.
      const tx =
        scaledW <= size.w
          ? (size.w - scaledW) / 2
          : Math.min(0, Math.max(size.w - scaledW, next.tx));
      const ty =
        scaledH <= size.h
          ? (size.h - scaledH) / 2
          : Math.min(0, Math.max(size.h - scaledH, next.ty));
      return { scale: next.scale, tx, ty };
    },
    [fit.w, fit.h, size.w, size.h],
  );

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setSize({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  /**
   * Sensible opening zoom.
   *
   * A wide screenshot letterboxed into a portrait phone fits to a thin,
   * illegible strip -- a 2000x877 image lands at roughly 375x164, which no one
   * can judge anything from. On a portrait viewport, open zoomed so the image
   * fills the height and the player pans sideways instead. Landscape and
   * desktop already show the whole thing usefully, so they open fitted.
   */
  const initialScale = useMemo(() => {
    if (!fit.h || !size.h) return 1;
    const portrait = size.h > size.w;
    if (!portrait) return 1;
    return Math.min(4, Math.max(1, size.h / fit.h));
  }, [fit.h, size.h, size.w]);

  const resetView = useCallback(() => {
    // Anchor to the left edge: the leftmost column is where reading starts.
    setView(clamp({ scale: initialScale, tx: 0, ty: 0 }));
  }, [clamp, initialScale]);

  // Re-fit whenever the fitted size changes (load, rotate, resize).
  useEffect(() => {
    if (!fit.w) return;
    resetView();
  }, [fit.w, fit.h, resetView]);

  /** Zoom while keeping the point under the cursor or fingers pinned. */
  const zoomAt = useCallback(
    (clientX: number, clientY: number, nextScale: number) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const v = viewRef.current;
      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
      const sx = clientX - rect.left;
      const sy = clientY - rect.top;
      const px = (sx - v.tx) / v.scale;
      const py = (sy - v.ty) / v.scale;
      setView(clamp({ scale, tx: sx - px * scale, ty: sy - py * scale }));
    },
    [clamp],
  );

  const onPointerDown = (event: React.PointerEvent) => {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      if (a && b) {
        gesture.current.mode = 'pinch';
        gesture.current.startDistance = Math.hypot(a.x - b.x, a.y - b.y);
        gesture.current.startView = viewRef.current;
      }
      return;
    }

    if (pointers.current.size === 1) {
      gesture.current.mode = 'pan';
      gesture.current.startX = event.clientX;
      gesture.current.startY = event.clientY;
      gesture.current.moved = 0;
      gesture.current.startView = viewRef.current;
    }
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const g = gesture.current;

    if (g.mode === 'pinch' && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      if (!a || !b || g.startDistance <= 0) return;
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, g.startView.scale * (distance / g.startDistance));
      return;
    }

    if (g.mode === 'pan') {
      const dx = event.clientX - g.startX;
      const dy = event.clientY - g.startY;
      g.moved = Math.max(g.moved, Math.hypot(dx, dy));
      setView(
        clamp({ scale: g.startView.scale, tx: g.startView.tx + dx, ty: g.startView.ty + dy }),
      );
    }
  };

  const onPointerUp = (event: React.PointerEvent) => {
    pointers.current.delete(event.pointerId);
    const g = gesture.current;

    if (pointers.current.size === 0) {
      g.mode = 'none';
      return;
    }
    // Dropping from two fingers to one: restart the pan from where we are, and
    // make sure the tail of a pinch can never be mistaken for a tap.
    if (pointers.current.size === 1) {
      const [remaining] = [...pointers.current.values()];
      if (remaining) {
        g.mode = 'pan';
        g.startX = remaining.x;
        g.startY = remaining.y;
        g.moved = TAP_SLOP;
        g.startView = viewRef.current;
      }
    }
  };

  const onWheel = (event: React.WheelEvent) => {
    const factor = event.deltaY < 0 ? 1.15 : 1 / 1.15;
    zoomAt(event.clientX, event.clientY, viewRef.current.scale * factor);
  };

  const zoomByButton = (factor: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, viewRef.current.scale * factor);
  };

  /**
   * An arrow's click fires after a pan that started on top of it, so a drag
   * would otherwise toggle a selection the player never intended.
   */
  const handleArrowClick = (arrowId: string) => {
    if (gesture.current.moved >= TAP_SLOP) return;
    onToggle(arrowId);
  };

  const inverse = 1 / view.scale;

  return (
    <div className="viewer">
      <div
        className="viewer__stage"
        ref={containerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
      >
        <div
          className="viewer__inner"
          style={{
            width: fit.w || undefined,
            height: fit.h || undefined,
            transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`,
          }}
        >
          <img className="viewer__img" src={image.src} alt={image.alt} draggable={false} />

          {arrows.map((arrow) => {
            const verdict = reveal?.get(arrow.id);
            const isSelected = selected.has(arrow.id);
            return (
              <button
                key={arrow.id}
                type="button"
                className={[
                  'arrow',
                  isSelected ? 'arrow--selected' : '',
                  verdict ? `arrow--${verdict}` : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={boxStyle(arrow.region)}
                aria-pressed={isSelected}
                aria-label={`${arrow.description}. ${
                  isSelected ? 'Marked as an error.' : 'Not marked.'
                }`}
                disabled={reveal !== undefined}
                onClick={() => handleArrowClick(arrow.id)}
              >
                <span
                  className="arrow__badge"
                  style={{ transform: `scale(${inverse})` }}
                  aria-hidden="true"
                >
                  {verdict ? VERDICT_GLYPH[verdict] : arrow.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="viewer__zoom">
        <button type="button" aria-label="Zoom out" onClick={() => zoomByButton(1 / 1.4)}>
          −
        </button>
        <button type="button" aria-label="Reset the view" onClick={resetView}>
          {Math.round(view.scale * 100)}%
        </button>
        <button type="button" aria-label="Zoom in" onClick={() => zoomByButton(1.4)}>
          +
        </button>
      </div>
    </div>
  );
}

const VERDICT_GLYPH: Record<Verdict, string> = {
  found: '✓',
  missed: '✗',
  'false-positive': '!',
  'correct-reject': '·',
};

function boxStyle(region: Hotspot) {
  return {
    left: `${region.x}%`,
    top: `${region.y}%`,
    width: `${region.w}%`,
    height: `${region.h}%`,
  };
}
